import { PRODUCT_AVAILABILITY, getProductAvailabilityStatus } from "../constants/availability";
export {
  DEFAULT_STOREFRONT_DECORATION_LEVEL,
  DEFAULT_STOREFRONT_THEME,
  STOREFRONT_AVATAR_FRAMES,
  STOREFRONT_AVATAR_FRAME_VALUES,
  STOREFRONT_DECORATION_LEVELS,
  STOREFRONT_DECORATION_LEVEL_VALUES,
  STOREFRONT_THEMES,
  STOREFRONT_THEME_VALUES,
  getStorefrontAvatarFrame,
  getStorefrontAvatarFrameAsset,
  getStorefrontDecorationLevel,
  getStorefrontTheme,
  getStorefrontThemeConfig,
  isValidStorefrontAvatarFrame,
  isValidStorefrontDecorationLevel,
  isValidStorefrontTheme,
} from "./storefrontThemes";

export const STOREFRONT_SLUG_FIELDS = ["publicSlug", "storefrontSlug", "slug"];
export const DEFAULT_STOREFRONT_ENABLED = false;

export const STOREFRONT_NAME_FIELDS = [
  "farmName",
  "businessName",
  "storeName",
  "displayName",
  "name",
];

export const STOREFRONT_BADGE_KEYS = {
  FOUNDING_MEMBER: "foundingMember",
  PROFILE_COMPLETE: "profileComplete",
  IDENTITY_VERIFIED: "identityVerified",
  FARM_VERIFIED: "farmVerified",
  BUSINESS_VERIFIED: "businessVerified",
  COMMUNITY_HELPER: "communityHelper",
  TRUSTED_SELLER: "trustedSeller",
};

export const normalizeStorefrontSlug = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/['"\u2019\u2018]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const getPublicStorefrontName = (profile = {}) =>
  STOREFRONT_NAME_FIELDS.map((field) => profile[field]).find(Boolean) ||
  "MyFarmingPal member";

export const hasPublicStorefrontName = (profile = {}) =>
  STOREFRONT_NAME_FIELDS.some((field) => Boolean(String(profile[field] || "").trim()));

export const getStoredStorefrontSlug = (profile = {}) => {
  for (const field of STOREFRONT_SLUG_FIELDS) {
    const cleanSlug = normalizeStorefrontSlug(profile[field]);
    if (cleanSlug) return cleanSlug;
  }

  return "";
};

export const getGeneratedStorefrontSlug = (profile = {}) => {
  for (const field of STOREFRONT_NAME_FIELDS) {
    const cleanSlug = normalizeStorefrontSlug(profile[field]);
    if (cleanSlug) return cleanSlug;
  }

  return "";
};

export const getStorefrontSlug = (profile = {}) =>
  getStoredStorefrontSlug(profile) || getGeneratedStorefrontSlug(profile);

export const isActivePublicFarmStand = (stand = {}) =>
  stand &&
  stand.isArchived !== true &&
  stand.archived !== true &&
  stand.isDeleted !== true &&
  stand.deleted !== true &&
  stand.isHidden !== true &&
  stand.hidden !== true &&
  stand.isActive !== false;

export const hasMeaningfulStorefrontData = (
  profile = {},
  { products = [], farmStands = [] } = {}
) => {
  if (!profile || typeof profile !== "object") return false;

  return (
    Boolean(getStoredStorefrontSlug(profile)) ||
    Boolean(profile.storefrontTheme) ||
    Boolean(profile.storefrontAvatarFrame) ||
    Boolean(profile.storefrontDecorationLevel) ||
    getPublicGalleryImages(profile, 1).length > 0 ||
    products.some(isActivePublicStorefrontProduct) ||
    farmStands.some(isActivePublicFarmStand)
  );
};

export const isStorefrontEnabled = (profile = {}, context = {}) => {
  if (profile?.storefrontEnabled === true) return true;
  if (profile?.storefrontEnabled === false) return false;

  return hasMeaningfulStorefrontData(profile, context);
};

export const getStorefrontUrl = (profile = {}, origin = "", context = {}) => {
  if (!isStorefrontEnabled(profile, context)) return "";
  const slug = getStorefrontSlug(profile);
  if (!slug) return "";
  const baseUrl =
    origin ||
    (typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "");
  return `${baseUrl}/farm/${slug}`;
};

export const getProfileSlug = (profile = {}) =>
  isStorefrontEnabled(profile) ? getStorefrontSlug(profile) : "";

export const getProfilePath = (profileOrId = {}) => {
  if (typeof profileOrId === "string") return `/profile/${profileOrId}`;

  const slug = getProfileSlug(profileOrId);
  if (slug) return `/profile/${slug}`;
  if (profileOrId?.id) return `/profile/${profileOrId.id}`;
  if (profileOrId?.uid) return `/profile/${profileOrId.uid}`;

  return "/profile";
};

export const getBroadStorefrontLocation = (profile = {}) => {
  const city = String(profile.city || "").trim();
  const county = String(profile.county || "").trim();
  const state = String(profile.state || "").trim();

  if (city && state) return `${city}, ${state}`;
  if (county && state) return `${county}, ${state}`;
  if (city) return city;
  if (county) return county;
  if (state) return state;
  return "";
};

export const getPublicGalleryImages = (profile = {}, maxImages = Infinity) => {
  if (!Array.isArray(profile.workImages)) return [];

  return profile.workImages
    .filter((image) => image?.url && image.isPrivate !== true && image.hidden !== true)
    .slice(0, maxImages);
};

export const isActivePublicStorefrontProduct = (product = {}) => {
  const status = String(product.status || "").toLowerCase();
  const availability = getProductAvailabilityStatus(product);

  return (
    product &&
    product.isArchived !== true &&
    product.archived !== true &&
    product.isDeleted !== true &&
    product.deleted !== true &&
    product.isHidden !== true &&
    product.hidden !== true &&
    status !== "archived" &&
    status !== "deleted" &&
    status !== "hidden" &&
    availability !== PRODUCT_AVAILABILITY.ARCHIVED
  );
};

export const getStorefrontReadiness = ({
  profile = {},
  products = [],
  galleryImages = getPublicGalleryImages(profile),
} = {}) => {
  const publicProducts = products.filter(isActivePublicStorefrontProduct);
  const items = [
    {
      key: "publicProfile",
      label: "Public profile enabled",
      complete: profile.isPublic !== false,
    },
    {
      key: "profilePhoto",
      label: "Profile photo exists",
      complete: Boolean(profile.profilePicture || profile.photoURL),
    },
    {
      key: "publicName",
      label: "Public name/farm/store name exists",
      complete: hasPublicStorefrontName(profile),
    },
    {
      key: "about",
      label: "Bio/About exists",
      complete: Boolean((profile.about || profile.bio || profile.intro || "").trim()),
    },
    {
      key: "broadLocation",
      label: "Broad location exists",
      complete: Boolean(getBroadStorefrontLocation(profile)),
    },
    {
      key: "publicContent",
      label: "At least one active public product or farm work photo exists",
      complete: publicProducts.length > 0 || galleryImages.length > 0,
    },
  ];

  return {
    ready: items.every((item) => item.complete),
    items,
    publicProducts,
    galleryImages,
  };
};

export const hasFoundingMemberBadge = (profile = {}) =>
  profile?.badges?.[STOREFRONT_BADGE_KEYS.FOUNDING_MEMBER] === true ||
  ((profile.founder === true ||
    profile.isFounder === true ||
    profile.founderAccount === true) &&
    profile.founderSpotActive !== false);

export const getPublicStorefrontBadges = ({
  profile = {},
  products = [],
  galleryImages = getPublicGalleryImages(profile),
} = {}) => {
  const readiness = getStorefrontReadiness({ profile, products, galleryImages });
  const badges = [];

  if (hasFoundingMemberBadge(profile)) {
    badges.push({
      key: STOREFRONT_BADGE_KEYS.FOUNDING_MEMBER,
      label: "Founding Member",
    });
  }

  if (
    profile?.badges?.[STOREFRONT_BADGE_KEYS.PROFILE_COMPLETE] === true ||
    readiness.ready
  ) {
    badges.push({
      key: STOREFRONT_BADGE_KEYS.PROFILE_COMPLETE,
      label: "Profile Complete",
      icon: "\uD83C\uDF31",
    });
  }

  return badges;
};
